import React, { useRef, useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/app/Layout";
import { Camera, Upload, Loader2, Pencil, UploadCloud, CheckCircle2, AlertCircle, X, Folder } from "lucide-react";
import { toast, Toaster } from "sonner";
import { compressImage } from "@/lib/imageCompress";

// Status for each file item in queue
const STATUS = { QUEUED: "queued", UPLOADING: "uploading", DONE: "done", ERROR: "error" };

export default function Scan() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const captureInputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [items, setItems] = useState([]); // {id, file, preview, status, invoice_id, error}
  const [reports, setReports] = useState([]);
  const [reportId, setReportId] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get("/reports", { params: { status: "active" } }).then(({ data }) => setReports(data)).catch(() => {});
  }, []);

  const addFiles = useCallback(async (files) => {
    const arr = Array.from(files || []).filter((f) => {
      const t = (f.type || "").toLowerCase();
      const name = (f.name || "").toLowerCase();
      return t.startsWith("image/") || name.endsWith(".heic") || name.endsWith(".heif");
    });
    if (!arr.length) { toast.error("Aucune image détectée"); return; }

    // Compress on the fly and build previews from the compressed version for accurate UI feedback
    const mapped = await Promise.all(arr.map(async (f) => {
      const compressed = await compressImage(f).catch(() => f);
      return {
        id: `${f.name}_${f.size}_${Math.random().toString(36).slice(2, 8)}`,
        file: compressed,
        preview: URL.createObjectURL(compressed),
        status: STATUS.QUEUED,
      };
    }));
    setItems((prev) => [...prev, ...mapped]);
  }, []);

  const removeItem = (id) => {
    setItems((prev) => {
      const found = prev.find((p) => p.id === id);
      if (found?.preview) URL.revokeObjectURL(found.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const clearAll = () => {
    items.forEach((it) => it.preview && URL.revokeObjectURL(it.preview));
    setItems([]);
  };

  const processOne = async (item) => {
    setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: STATUS.UPLOADING } : p)));
    try {
      const fd = new FormData();
      fd.append("file", item.file);
      if (reportId) fd.append("report_id", reportId);
      const { data } = await api.post("/invoices/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => prev.map((p) => (p.id === item.id ? {
        ...p,
        status: data.ocr_error ? STATUS.DONE : STATUS.DONE,
        invoice_id: data.invoice_id,
        warning: data.ocr_error,
        extracted: {
          date: data.date,
          merchant: data.merchant,
          total: data.total,
          tps: data.tps,
          tvq: data.tvq,
          province: data.province,
          category: data.category,
          is_restaurant: data.is_restaurant,
        },
      } : p)));
      return { ok: true };
    } catch (e) {
      const msg = e?.response?.data?.detail || "Échec";
      setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, status: STATUS.ERROR, error: msg } : p)));
      return { ok: false };
    }
  };

  const runAll = async () => {
    const queued = items.filter((i) => i.status === STATUS.QUEUED);
    if (!queued.length) return;
    setProcessing(true);
    const CONCURRENCY = 10;
    const results = { ok: 0, err: 0 };
    // Worker pool pattern
    let idx = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, queued.length) }, async () => {
      while (idx < queued.length) {
        const current = queued[idx++];
        const r = await processOne(current);
        if (r.ok) results.ok++; else results.err++;
      }
    });
    await Promise.allSettled(workers);
    setProcessing(false);
    toast.success(`${results.ok} extraite(s)${results.err ? `, ${results.err} erreur(s)` : ""}`);
    if (queued.length === 1 && results.ok === 1) {
      // Direct navigation if only one
      setTimeout(() => {
        setItems((prev) => {
          const done = prev.find((i) => i.status === STATUS.DONE);
          if (done?.invoice_id) navigate(`/invoice/${done.invoice_id}`);
          return prev;
        });
      }, 100);
    }
  };

  // Drag & drop handlers
  const onDragOver = useCallback((e) => { e.preventDefault(); setDragging(true); }, []);
  const onDragLeave = useCallback((e) => { e.preventDefault(); setDragging(false); }, []);
  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    addFiles(e.dataTransfer?.files);
  }, [addFiles]);

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <div className="max-w-4xl mx-auto">
        <p className="overline mb-2">Scanner une facture</p>
        <h1 className="font-heading text-3xl sm:text-5xl font-black tracking-tighter leading-tight sm:leading-none mb-3">
          Prenez une photo. Le reste est automatique.
        </h1>
        <p className="text-gray-500 mb-8 max-w-xl leading-relaxed">
          L'IA lit la <strong>date</strong>, le <strong>montant</strong>, les <strong>taxes (TPS/TVQ)</strong> et <strong>classe</strong> la facture dans la bonne catégorie (restaurant, essence, hôtel...). Vous pouvez scanner plusieurs factures d'un coup.
        </p>

        {/* How it works */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          {[
            { n: "01", t: "Ajoutez vos photos", d: "Glissez-déposez, choisissez des fichiers, ou prenez une photo avec la caméra." },
            { n: "02", t: "L'IA analyse (×10)", d: "Chaque facture est extraite en parallèle en ~3 secondes." },
            { n: "03", t: "Vérifiez et modifiez", d: "Cliquez sur une facture pour corriger ou compléter les champs." },
          ].map((s) => (
            <div key={s.n} className="border-t-2 border-[#0B0F19] pt-3">
              <div className="overline mb-1">{s.n}</div>
              <div className="font-heading font-bold text-sm mb-1">{s.t}</div>
              <div className="text-xs text-gray-600 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>

        {/* Report selector */}
        <div className="ra-card p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="overline flex items-center gap-2"><Folder size={14} /> Rapport cible</label>
          <select
            className="ra-input sm:w-72"
            value={reportId}
            onChange={(e) => setReportId(e.target.value)}
            data-testid="scan-report-select"
          >
            <option value="">— Aucun (non assigné) —</option>
            {reports.map((r) => (
              <option key={r.report_id} value={r.report_id}>{r.name}</option>
            ))}
          </select>
          <span className="text-xs text-gray-500">Toutes les factures scannées seront assignées à ce rapport.</span>
        </div>

        {/* Dropzone */}
        <div
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          className={`ra-card p-10 text-center border-2 border-dashed transition-colors ${dragging ? "border-[#002FA7] bg-blue-50/40" : "border-gray-300"}`}
          data-testid="dropzone"
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-[#002FA7] text-white flex items-center justify-center">
            <UploadCloud size={22} />
          </div>
          <div className="font-heading text-2xl font-bold tracking-tight mb-1">
            {dragging ? "Relâchez pour ajouter" : "Glissez vos photos ici"}
          </div>
          <p className="text-sm text-gray-500 mb-6">JPEG, PNG, WEBP, HEIC — plusieurs fichiers acceptés</p>
          <div className="flex flex-wrap justify-center gap-2">
            <button className="btn-primary" onClick={() => fileInputRef.current?.click()} data-testid="upload-button">
              <Upload size={14} /> Choisir des fichiers
            </button>
            <button className="btn-secondary" onClick={() => captureInputRef.current?.click()} data-testid="camera-button">
              <Camera size={14} /> Photo caméra
            </button>
            <button className="btn-secondary" onClick={() => navigate("/invoice/new")} data-testid="manual-entry-button">
              <Pencil size={14} /> Manuel
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            data-testid="upload-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <input
            ref={captureInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            data-testid="camera-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
        </div>

        {/* Queue */}
        {items.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="overline">File d'attente · Parallélisme ×10</p>
                <div className="font-heading text-2xl font-bold tracking-tight">
                  {items.filter((i) => i.status === STATUS.DONE).length} / {items.length} extraites
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button className="btn-ghost" onClick={clearAll} disabled={processing} data-testid="clear-queue-button">
                  <X size={14} /> Vider
                </button>
                <button
                  className="btn-primary"
                  onClick={runAll}
                  disabled={processing || items.every((i) => i.status !== STATUS.QUEUED)}
                  data-testid="process-queue-button"
                >
                  {processing ? <><Loader2 size={14} className="animate-spin" /> Analyse parallèle...</> : <><UploadCloud size={14} /> Analyser tout (×10)</>}
                </button>
              </div>
            </div>

            {(processing || items.some((i) => i.status !== STATUS.QUEUED)) && (
              <div className="mb-4" data-testid="batch-progress">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1 num">
                  <span>{items.filter((i) => i.status === STATUS.UPLOADING).length} en cours</span>
                  <span>
                    {items.filter((i) => i.status === STATUS.DONE).length} / {items.length}
                    {items.filter((i) => i.status === STATUS.ERROR).length > 0 && (
                      <span className="text-red-600 ml-2">{items.filter((i) => i.status === STATUS.ERROR).length} erreur(s)</span>
                    )}
                  </span>
                </div>
                <div className="h-1.5 bg-gray-200 overflow-hidden">
                  <div
                    className="h-full bg-[#002FA7] transition-all duration-300"
                    style={{ width: `${items.length === 0 ? 0 : Math.round((items.filter((i) => i.status === STATUS.DONE || i.status === STATUS.ERROR).length / items.length) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="queue-grid">
              {items.map((it) => (
                <div key={it.id} className="ra-card overflow-hidden" data-testid={`queue-item-${it.id}`}>
                  <div className="relative aspect-[3/4] bg-gray-50">
                    <img src={it.preview} alt="" className="w-full h-full object-cover" />
                    <div className="absolute top-2 right-2 flex gap-1">
                      {it.status === STATUS.QUEUED && (
                        <button onClick={() => removeItem(it.id)} className="w-7 h-7 bg-white border border-gray-300 flex items-center justify-center hover:bg-red-50 hover:border-red-500">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 px-2 py-1.5 bg-white/90 backdrop-blur-sm flex items-center gap-2">
                      {it.status === STATUS.QUEUED && <span className="overline text-gray-600">EN ATTENTE</span>}
                      {it.status === STATUS.UPLOADING && (
                        <><Loader2 size={12} className="animate-spin text-[#002FA7]" /><span className="overline text-[#002FA7]">ANALYSE</span></>
                      )}
                      {it.status === STATUS.DONE && (
                        <><CheckCircle2 size={12} className="text-green-600" /><span className="overline text-green-700">EXTRAITE</span></>
                      )}
                      {it.status === STATUS.ERROR && (
                        <><AlertCircle size={12} className="text-red-600" /><span className="overline text-red-700">ERREUR</span></>
                      )}
                    </div>
                  </div>
                  <div className="p-2 text-xs truncate" title={it.file.name}>{it.file.name}</div>
                  {it.extracted && (
                    <div className="px-2 pb-2 border-t border-gray-100 pt-2 space-y-1 text-xs" data-testid={`extracted-${it.id}`}>
                      {it.warning && (
                        <div className="mb-1 p-1.5 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-sm">
                          ⚠ {it.warning}
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Date</span>
                        <span className="num font-semibold">{it.extracted.date || <span className="text-red-500">—</span>}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Fournisseur</span>
                        <span className="font-medium truncate max-w-[110px]" title={it.extracted.merchant || ""}>{it.extracted.merchant || <span className="text-red-500">—</span>}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Total</span>
                        <span className="num font-semibold text-[#002FA7]">{Number(it.extracted.total || 0).toFixed(2)} $</span>
                      </div>
                      {it.extracted.is_restaurant && <span className="badge restaurant">Restaurant</span>}
                    </div>
                  )}
                  {it.status === STATUS.DONE && it.invoice_id && (
                    <div className="p-2 border-t border-gray-100">
                      <button className="btn-ghost w-full text-xs" onClick={() => navigate(`/invoice/${it.invoice_id}`)} data-testid={`open-result-${it.id}`}>
                        Voir et éditer →
                      </button>
                    </div>
                  )}
                  {it.status === STATUS.ERROR && (
                    <div className="p-2 border-t border-gray-100 text-xs text-red-600">{it.error}</div>
                  )}
                </div>
              ))}
            </div>

            {items.some((i) => i.status === STATUS.DONE) && (
              <div className="mt-6">
                <button className="btn-secondary" onClick={() => navigate("/dashboard")} data-testid="goto-dashboard-button">
                  Voir toutes mes factures →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
